import { useContext, useEffect, useState, cloneElement } from "react";
import { createPortal } from "react-dom";
import {
  Text,
  Heading,
  Content,
  ProgressCircle,
  ButtonGroup,
  Button,
  IllustratedMessage,
  mergeStyles,
} from "@react-spectrum/s2";
import CloudStateErrorIcon from "@react-spectrum/s2/illustrations/linear/CloudStateError";
import ChevronLeftIcon from "@react-spectrum/s2/icons/ChevronLeft";

import { VideoWizardContext } from "@app/views/projects/videoWizardContext";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

/**
 * Shared centered heading block used at the top of most wizard steps.
 * Exported so steps that need the raw wrapper (e.g. bespoke loading blocks)
 * can reuse it directly.
 */
export const topHeadingStyle = style({
  gridArea: "content",

  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginTop: 44,
  marginBottom: 96,
  maxWidth: 720,
  marginX: "auto",
  textWrap: "pretty",
  textAlign: "center",
});

/**
 * Default styling for a step's footer element (e.g. the EditPrompt bar).
 * Steps pass this to the footer child via its `styles` prop.
 */
export const stepFooterStyle = style({
  gridArea: "footer",
  justifySelf: "end",

  display: "flex",
  flexDirection: "column",
  gap: 12,

  width: "full",
  maxWidth: 1024,

  marginY: 24,
  marginX: "auto",

  position: "sticky",
  bottom: 24,
});

export const stepFooterPromptStyles = style({
  boxSizing: "border-box",
  width: "full",
  padding: 16,
  backgroundColor: "elevated",
  borderRadius: "xl",
  boxShadow: "elevated",
});

const defaultContentStyle = style({
  gridArea: "content",
  display: "flex",
  flexDirection: "column",
  gap: 32,
  size: "full",
});

/**
 * Unified layout wrapper for a wizard step.
 *
 * Renders the step's content into the `content` grid area of the
 * VideoWizard layout. Pass `styles` (a `style({...})` result that
 * includes `gridArea: "content"`) to fully override the default flex layout
 * for steps that need a custom container (grid, constrained width, etc.).
 *
 * Compound helpers handle the boilerplate every step repeats:
 * - `Step.Header`  — centered title/subtitle block
 * - `Step.Loading` — "Preparing your …" state with spinner
 * - `Step.Error`   — illustrated error state with a retry action
 * - `Step.Actions` — portals the actions bar into the shared actions container
 * - `Step.Footer`  — portals a footer element into the layout's footer area
 */
export const Step = ({ children, styles }) => (
  <div className={styles ?? defaultContentStyle}>{children}</div>
);

const StepHeader = ({ title, subtitle, children, styles }) => (
  <div className={mergeStyles(topHeadingStyle, styles)}>
    {!!title && (
      <Heading styles={style({ font: "heading-xl", marginY: 0 })}>
        {title}
      </Heading>
    )}
    {!!subtitle && <Text styles={style({ font: "body-xl" })}>{subtitle}</Text>}
    {children}
  </div>
);

const StepLoading = ({ title, subtitle, icon, ariaLabel = "Loading" }) => (
  <div className={mergeStyles(topHeadingStyle, style({ alignSelf: "center" }))}>
    {!!title && (
      <Heading styles={style({ font: "heading-xl", marginY: 0 })}>
        {title}
      </Heading>
    )}
    {!!subtitle && <Text styles={style({ font: "body-xl" })}>{subtitle}</Text>}
    {!!icon &&
      cloneElement(icon, {
        styles: style({ size: 160, marginTop: 16 }),
      })}
    <ProgressCircle
      aria-label={ariaLabel}
      isIndeterminate
      staticColor="auto"
      size="L"
      styles={style({ alignSelf: "center", marginY: 32 })}
    />
  </div>
);

const StepError = ({ title, children, onRetry, retryLabel = "Try again" }) => (
  <IllustratedMessage size="L" styles={style({ marginY: 96, marginX: "auto" })}>
    <CloudStateErrorIcon />
    <Heading>{title}</Heading>
    {!!children && <Content>{children}</Content>}
    {!!onRetry && (
      <ButtonGroup>
        <Button onPress={onRetry}>
          <Text>{retryLabel}</Text>
        </Button>
      </ButtonGroup>
    )}
  </IllustratedMessage>
);

/**
 * Renders the standard step actions bar (an optional Back button, optional
 * middle content, and an optional primary action) into the shared actions
 * container rendered by VideoWizard.
 *
 * Pass `onBack` to show the icon-only Back button and `onContinue` to show the
 * primary action. `continueLabel`/`continueVariant`/`isContinueDisabled`
 * customize the primary action; `children` renders between the two (e.g. a
 * consent checkbox). A mount effect forces a re-render so the portal attaches
 * even when the ref is still null on the step's first paint.
 */
const StepActions = ({
  onBack,
  backLabel = "Back",
  onContinue,
  continueLabel = "Continue",
  continueVariant,
  isContinueDisabled = false,
  children,
}) => {
  const { actionsContainerRef } = useContext(VideoWizardContext);
  const [, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!actionsContainerRef?.current) {
    return null;
  }

  return createPortal(
    <ButtonGroup>
      {!!onBack && (
        <Button aria-label={backLabel} variant="secondary" onPress={onBack}>
          <ChevronLeftIcon />
        </Button>
      )}
      {children}
      {!!onContinue && (
        <Button
          variant={continueVariant}
          isDisabled={isContinueDisabled}
          onPress={onContinue}
        >
          {continueLabel}
        </Button>
      )}
    </ButtonGroup>,
    actionsContainerRef.current,
  );
};

/**
 * Portals a footer element into the layout grid container so it can occupy the
 * `footer` grid area regardless of how deeply the step nests its content.
 */
const StepFooter = ({ children }) => {
  const { contentContainerRef } = useContext(VideoWizardContext);
  const [, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return contentContainerRef?.current
    ? createPortal(children, contentContainerRef.current)
    : null;
};

Step.Header = StepHeader;
Step.Loading = StepLoading;
Step.Error = StepError;
Step.Actions = StepActions;
Step.Footer = StepFooter;
